import React, { useCallback, useEffect, useState } from 'react';
import { Button, Flex, Modal, Tabs, TabsProps } from 'antd';
import { Component } from '../../../common/componentUtils';
import { t } from '../../../utils';
import { EMPTY_TEXT, SignatureSave, SignatureState, SignatureTab } from './constants';
import { ESignaturePad } from './ESignaturePad/ESignaturePad';
import { ESignatureTextPad } from './ESignatureTextPad/ESignatureTextPad';
import { SaveSignatureEvent } from './SaveSignatureEvent';

const PEN_COLORS = { black: 'rgb(51,51,51)', blue: 'rgb(0,29,163)' };

interface SignatureModalBodyProps {
	closeModal: () => void;
	updateComponent: (signatureResponse: any) => void;
	deleteComponent: () => void;
	componentsToEdit: Component[];
}
interface AdditionalModalProps {
	showModal?: boolean;
}

type Props = SignatureModalBodyProps & AdditionalModalProps;

export function ESignatureAnnotationModal({
	showModal,
	closeModal,
	deleteComponent,
	updateComponent,
	componentsToEdit,
}: Props) {
	const [isModalShown, setIsModalShown] = useState(false);

	useEffect(() => {
		if (!isModalShown) {
			setIsModalShown(true);

			if (!document.getElementById('Cedarville-Cursive')) {
				const link = document.createElement('link');
				link.type = 'text/css';
				link.href = '//fonts.googleapis.com/css?family=Cedarville+Cursive';
				link.rel = 'stylesheet';
				link.id = 'Cedarville-Cursive';
				document.head.appendChild(link);
			}
		}
	}, [
		isModalShown,
		showModal,
		closeModal,
		updateComponent,
		deleteComponent,
		componentsToEdit,
	]);
	return (
		<SignatureModalBody
			closeModal={closeModal}
			updateComponent={updateComponent}
			deleteComponent={deleteComponent}
			componentsToEdit={componentsToEdit}
		/>
	);
}

const SignatureModalBody = (props: SignatureModalBodyProps) => {
	//TODO
	// Get signature color from user or acount settings
	const signatureColor = 'black';

	const { closeModal, updateComponent, deleteComponent, componentsToEdit } = props;
	const componentForEdit = componentsToEdit[0];

	const savedSignatureJsonData: string = componentsToEdit?.[0]?.signatureJsonData;
	const savedSignatureText: string = componentForEdit?.signatureText || EMPTY_TEXT;
	const isSignatureAlreadySaved = savedSignatureJsonData || savedSignatureText;

	const drawnSignatureState = savedSignatureJsonData
		? SignatureState.SAVED_SIGNATURE
		: SignatureState.NOT_SIGNED;
	const typedSignatureState = savedSignatureText
		? SignatureState.SAVED_SIGNATURE
		: SignatureState.NOT_SIGNED;

	const [signatureSignedByTab, setSignatureSignedByTab] = useState<
		Record<SignatureTab, SignatureState>
	>({
		[SignatureTab.DRAWN]: drawnSignatureState,
		[SignatureTab.TYPED]: typedSignatureState,
	});

	const signatureType = componentForEdit?.signatureType as SignatureTab;

	const [selectedTab, setSelectedTab] = useState<SignatureTab>(
		signatureType || SignatureTab.DRAWN
	);

	const saveSignature = useCallback(
		async (signatureDataUrl: string, jsonData: string, text: string) => {
			const payload = {
				signature: {
					color: signatureColor,
					image_data: null,
					image_data_raw: signatureDataUrl,
					image_url: null,
					is_default: false,
					json_data: jsonData,
					s3_guid: null,
					signature_text: text,
					signature_type: selectedTab,
					signer_id: null,
					user_id: null,
				},
			};

			updateComponent(payload.signature);
		},
		[selectedTab, updateComponent]
	);

	const onApply = () => {
		if (signatureSignedByTab[selectedTab] === SignatureState.SIGNED) {
			SaveSignatureEvent.dispatch({
				id: componentForEdit.id,
				message: SignatureSave.STARTED,
				signatureType: selectedTab,
			});
		}
		closeModal();
	};

	const onDismiss = () => {
		// NOTE: Component should not be deleted when signature was already saved

		// handles cases when signature was not saved - Non Edit flow
		// case 1. Signature modal was opened and clicked close button without signing.
		// case 2. Signature modal was opened, signature was typed or drawn and clicked close button
		// case 3. Signature modal was opened, signature was signed and then cleared and finally clicked close button

		if (
			!isSignatureAlreadySaved &&
			signatureSignedByTab[selectedTab] !== SignatureState.SAVED_SIGNATURE
		) {
			deleteComponent();
		}

		closeModal();
	};

	const onTabChange = (key: SignatureTab) => {
		setSelectedTab(key);
	};

	const setIsSignatureSigned = (value: SignatureState) => {
		setSignatureSignedByTab({
			...signatureSignedByTab,
			[selectedTab]: value,
		});
	};

	const items: TabsProps['items'] = [
		{
			key: SignatureTab.DRAWN,
			label: t('draw'),
			children: (
				<ESignaturePad
					penColor={PEN_COLORS[signatureColor!]}
					saveSignature={saveSignature}
					savedSignatureJsonData={savedSignatureJsonData}
					isSignatureSigned={signatureSignedByTab[SignatureTab.DRAWN]}
					setIsSignatureSigned={setIsSignatureSigned}
				/>
			),
		},
		{
			key: SignatureTab.TYPED,
			label: t('type'),
			children: (
				<ESignatureTextPad
					penColor={PEN_COLORS[signatureColor!]}
					saveSignature={saveSignature}
					savedSignatureText={savedSignatureText}
					isSignatureSigned={signatureSignedByTab[SignatureTab.TYPED]}
					setIsSignatureSigned={setIsSignatureSigned}
				/>
			),
		},
	];

	return (
		<Modal
			onCancel={onDismiss}
			open
			maskClosable={false}
			title={t('addSignature')}
			footer={
				<Flex style={{ padding: '20px 0 10px' }}>
					<Button
						type="primary"
						onClick={onApply}
						data-testid="apply"
						disabled={signatureSignedByTab[selectedTab] === SignatureState.NOT_SIGNED}
					>
						{t('apply')}
					</Button>
					<Button onClick={onDismiss} data-testid="close">
						{t('close')}
					</Button>
				</Flex>
			}
			width={848}
			centered
		>
			<Tabs
				defaultActiveKey="1"
				items={items}
				onChange={key => onTabChange(key as SignatureTab)}
				activeKey={selectedTab}
			/>
		</Modal>
	);
};
